import { useState } from "react";
import { X, Download, Loader2, RefreshCw } from "@semo/icons";
import { Button } from "@semo/shared-ui";
import { toast } from "sonner";
import {
  useContractsControllerGetContract,
  contractPdfPath,
  downloadPdf,
  type ContractResponseDto,
} from "@semo/api-contracts";
import {
  ContractPdfDocument,
  mapContractToContractPdfData,
} from "@semo/shared-pdf-components";
import { formatDateKorean } from "@semo/shared-utils";
import { cn } from "@semo/shared-ui";
import { pdfDownloadErrorMessage } from "../../utils/chat-error-messages";

export interface ContractSheetModalProps {
  contractId: string;
  onClose: () => void;
  /** Dependency Injection for data fetching hook */
  useContractHook?: (id: string) => {
    data: any; // Using any to handle different response wrappers while keeping it simple
    isLoading: boolean;
    error?: any;
  };
}

export function ContractSheetModal({
  contractId,
  onClose,
  useContractHook,
}: ContractSheetModalProps) {
  // Use injected hook or fallback to default
  const defaultHook = useContractsControllerGetContract;
  const { data: response, isLoading } = useContractHook
    ? useContractHook(contractId)
    : (defaultHook as any)(contractId);

  // 🎯 Robust data extraction:
  // 1. response?.data?.data (Standard BaseResponseDto from Orval)
  // 2. response?.data (Custom hook with axios response wrapper)
  // 3. response (Direct DTO)
  const contract = (response?.data?.id
    ? response.data
    : response?.data || response) as unknown as ContractResponseDto;

  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  const date = contract ? formatDateKorean(contract.createdAt) : "";
  const pdfUrl = contract?.pdfUrl;
  const contractNumber = contract?.contractNumber;

  /**
   * 계약서를 서버 프록시에서 받아 저장한다.
   *
   * 저장된 `pdfUrl` 은 비공개 버킷을 가리키는 주소라 링크로 걸면 안 된다.
   * 아직 PDF 가 없으면 프록시가 그 자리에서 만들어 준다 — 그래서 별도의
   * 「생성 요청」 왕복이 필요 없다.
   */
  const handlePdfDownload = async () => {
    const fileName = `전자계약서_${contractNumber}_${date.replace(/\./g, "")}.pdf`;

    setIsGeneratingPdf(true);
    try {
      await downloadPdf(contractPdfPath(contractId), fileName);
    } catch (error) {
      console.error("PDF download/generation failed:", error);
      // 서버가 파일을 주지 못함 → 매뉴얼 R06 5.2.4.4 1번, 그 밖의 처리 오류 → 2번
      toast.error(pdfDownloadErrorMessage(error));
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  // Show loading state
  if (isLoading || !contract) {
    return (
      <div className="fixed inset-0 z-[210] flex items-center justify-center p-2 sm:p-4 bg-slate-900/60  backdrop-blur-sm">
        <div
          className={cn(
            "bg-white  rounded-lg p-8 text-center",
            "animate-in fade-in zoom-in-95 duration-200",
          )}
        >
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-sm text-slate-600 ">계약서를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  // Contract 데이터를 PDF 컴포넌트 형식으로 변환
  const pdfData = mapContractToContractPdfData(
    {
      contractNumber: contract.contractNumber,
      createdAt: contract.createdAt,
      totalAmount: contract.totalAmount,
      status: contract.status,
      pdfUrl: contract.pdfUrl ?? undefined,
      supplier: contract.supplier
        ? {
            name: contract.supplier.name,
            businessNumber: contract.supplier.businessNumber,
            ceoName: contract.supplier.ceoName,
            address: contract.supplier.address,
            phone: contract.supplier.phone,
            email: contract.supplier.email,
            managerName: contract.supplier.managerName,
            managerPhone: contract.supplier.managerPhone,
            companySealUrl: contract.supplier.companySealUrl,
          }
        : undefined,
      buyer: contract.buyer
        ? {
            name: contract.buyer.name,
            phone: contract.buyer.phone,
            email: contract.buyer.email,
            // 「귀중」 자리와 그 아래 대표자·사업자번호·주소는 발주«기관» 정보다.
            // 담당자 개인 이름(name)으로 폴백하면 다운로드한 PDF 와 다른 상대가 보인다.
            company: contract.buyer.company,
            representativeName: contract.buyer.representativeName,
            businessNumber: contract.buyer.businessNumber,
            address: contract.buyer.address,
          }
        : undefined,
      deliveryAddress: contract.deliveryAddress
        ? {
            province: contract.deliveryAddress.province,
            city: contract.deliveryAddress.city,
            district: contract.deliveryAddress.district,
            streetAddress: contract.deliveryAddress.streetAddress,
            addressDetail: contract.deliveryAddress.addressDetail,
            postalCode: contract.deliveryAddress.postalCode,
            recipientName: contract.deliveryAddress.recipientName,
            recipientPhone: contract.deliveryAddress.recipientPhone,
          }
        : undefined,
      items: contract.items?.map((item) => ({
        name: item.name,
        quantity: item.quantity,
        unit: item.unit,
        unitPrice: item.quantity > 0 ? item.amount / item.quantity : 0,
        amount: item.amount,
        // 규격·특이사항을 넘기지 않으면 화면 계약서만 규격이 «-», 특이사항이 고정 예시 문구로
        // 찍혀 [다운로드] PDF 와 달랐다(GS A3-05). PDF 와 같은 값을 쓴다.
        spec1: item.spec1 ?? undefined,
        spec2: item.spec2 ?? undefined,
        spec3: item.spec3 ?? undefined,
      })),
      // 계약서 PDF 의 특이사항과 같은 출처·같은 기본 문구 — 계약한 견적 버전의 비고(description).
      // (서버 contract-pdf.service 가 비었을 때 «상기 조건으로 계약합니다.» 를 쓴다)
      notes: contract.description?.trim()
        ? contract.description
        : "상기 조건으로 계약합니다.",
    },
    { dateFormatter: formatDateKorean },
  );

  return (
    <div
      className={cn(
        "bg-white ",
        "w-full h-full sm:h-full sm:w-full flex flex-col overflow-hidden",
      )}
    >
      {/* Header Toolbar */}
      <div className="h-[56px] sm:h-[60px] border-b border-slate-200  flex items-center justify-between px-3 sm:px-6 bg-slate-50  shrink-0">
        <h2 className="text-sm sm:text-base font-semibold text-slate-900 ">
          전자 계약서 상세
        </h2>
        <div className="flex items-center gap-1 sm:gap-2">
          <Button
            variant="outline"
            size="sm"
            className="h-8 sm:h-9 gap-1 sm:gap-2 bg-white  text-xs sm:text-sm px-2 sm:px-3"
            onClick={handlePdfDownload}
            disabled={isGeneratingPdf}
          >
            {isGeneratingPdf ? (
              <>
                <Loader2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 animate-spin" />
                <span className="hidden sm:inline">생성 중...</span>
              </>
            ) : pdfUrl ? (
              <>
                <Download className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">다운로드</span>
              </>
            ) : (
              <>
                <RefreshCw className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">PDF 생성 및 다운로드</span>
              </>
            )}
          </Button>
          <div className="w-px h-4 bg-slate-300  mx-0.5 sm:mx-1" />
          <button
            onClick={onClose}
            className="p-1.5 sm:p-2 hover:bg-slate-200 :bg-slate-700 rounded-lg transition-colors"
          >
            <X className="w-4 h-4 sm:w-5 sm:h-5 text-slate-500 " />
          </button>
        </div>
      </div>

      {/* Document Content - 공유 컴포넌트 사용 */}
      <div className="flex-1 overflow-y-auto bg-[#f2f5f9]  p-2 sm:p-4 md:p-8 custom-scrollbar">
        <div className="mx-auto max-w-[210mm] w-full bg-white border border-[#e8edf3] rounded-[8px] sm:rounded-[14px] shadow-[0_16px_50px_rgba(17,24,39,.08)] overflow-hidden">
          <ContractPdfDocument data={pdfData} isPdf={false} isBlur={false} />
        </div>
      </div>
    </div>
  );
}
